import React, { useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from './moment'
import DateStepper from './DateStepper'
import DayCalendar from './DayCalendar'
import DayPickerStepperLabel from './DayPickerStepperLabel'
import { useNavigation, FORMAT, useStartOfDay } from './utils'
import { useGlobalId, useSetState } from '@zambezi/sdk/common'

/**
 * Create a React Day Picker component
 *
 * @param {import('./interfaces').DayPickerProps} props
 * @returns {JSX.Element}
 */
function DayPicker ({
  minDate,
  maxDate,
  disableDayFn,
  onYearClick,
  onMonthClick,
  onDaySelected,
  idPrefix,
  date: propsDate,
  displayedDate,
  defaultDate,
  withKeyboardNavigation,
  onFocusChanged,
  onFocusedDayRef
}) {
  const currentDay = useStartOfDay()
  const [{ date, focusedDay }, setState] = useSetState({
    date: propsDate === undefined ? defaultDate || currentDay : propsDate,
    focusedDay: getDefaultFocusedDay()
  })
  const onNavigationRef = useRef()
  const globalId = useGlobalId('pika-title-')
  const id = idPrefix ? `pika-title-${idPrefix}` : globalId
  const elementRef = useRef()
  const momentDate = moment(focusedDay || date)
  const firstDayOfMonth = momentDate.clone().startOf('month').valueOf()
  const focusedDayRef = useRef()
  const selectedMonth = moment.localeData().months()[momentDate.month()]
  const selectedYear = momentDate.year()
  const headingText = useMemo(
    () => `${selectedMonth} ${selectedYear}`,
    [selectedMonth, selectedYear]
  )

  useEffect(updateFocusedDayRef, [focusedDayRef.current, focusedDay])
  useEffect(updateNavigation)
  useEffect(updateDate, [propsDate, displayedDate])
  useNavigation(onNavigationRef, elementRef)

  return (
    <div className='zb-date-picker pika-single'>
      <section className='pika-lendar'>
        <h2
          id={id}
          className='zb-slider-description-sr-only'
          aria-live='polite'
          aria-atomic='true'
        >
          {headingText}
        </h2>
        <DateStepper
          className='pika-title'
          date={firstDayOfMonth}
          renderLabel={date => (
            <DayPickerStepperLabel
              date={date}
              onYearClick={onYearClick}
              onMonthClick={onMonthClick}
            />
          )}
          onPrevDate={handleMonthChange}
          onNextDate={handleMonthChange}
          stepUnit='months'
          prevDisabled={isPrevDisabled()}
          nextDisabled={isNextDisabled()}
        />
      </section>
      <section className='zb-month-calendar' ref={elementRef}>
        <DayCalendar
          aria-labelledby={id}
          isDayDisabled={isDayDisabled}
          isDaySelected={isDaySelected}
          onDaySelected={handleDaySelected}
          date={date}
          currentDay={currentDay}
          focusedDay={focusedDay}
          firstDayOfWeek={1}
          idPrefix={idPrefix || globalId}
          focusedDayRef={focusedDayRef}
        />
      </section>
    </div>
  )

  function isPrevDisabled () {
    return minDate && momentDate.clone().date(0).isBefore(minDate)
  }

  function isNextDisabled () {
    return (
      maxDate &&
      momentDate.clone().endOf('month').add(1, 'day').isAfter(maxDate)
    )
  }

  function isDaySelected (d) {
    if (isDayDisabled(date)) return false
    return moment(d).isSame(date, 'day')
  }

  function getDefaultFocusedDay () {
    return (
      displayedDate ||
      (propsDate === undefined ? defaultDate || currentDay : propsDate)
    )
  }

  function updateNavigation () {
    onNavigationRef.current = withKeyboardNavigation && onNavigation
  }

  function updateDate () {
    if (propsDate !== undefined) {
      setState({
        date: propsDate,
        focusedDay: getDefaultFocusedDay()
      })
    } else {
      setState({
        focusedDay: getDefaultFocusedDay()
      })
    }
  }

  function onNavigation (keyCode, inside, modifierKeys = [], event) {
    if (modifierKeys.includes('Shift')) {
      switch (keyCode) {
        case 'page up':
          updateFocusState({
            focusedDay: moment(focusedDay || date)
              .add(1, 'years')
              .toDate()
          })
          break
        case 'page down':
          updateFocusState({
            focusedDay: moment(focusedDay || date)
              .subtract(1, 'years')
              .toDate()
          })
          break
      }
      return
    }

    const steps = {
      left: -1,
      right: 1,
      up: -7,
      down: 7,
      'page up': 1,
      'page down': -1
    }

    if (keyCode in steps) {
      const newDate = moment(focusedDay || date)
      let newFocusedDay = newDate.add(steps[keyCode], 'days').toDate()

      if (keyCode === 'page up' || keyCode === 'page down') {
        newFocusedDay = newDate.add(steps[keyCode], 'month').toDate()
      }
      updateFocusState({
        focusedDay: newFocusedDay
      })
    } else {
      switch (keyCode) {
        case 'enter':
          if (inside) {
            handleDaySelected(focusedDay || date)
          }
          break
        case 'esc':
          handleDaySelected(null)
          break
        case 'home':
          updateFocusState({ focusedDay: new Date(firstDayOfMonth) })
          break
        case 'end':
          updateFocusState({
            focusedDay: momentDate
              .clone()
              .endOf('month')
              .startOf('day')
              .toDate()
          })
          break
      }
    }
  }

  function handleDaySelected (date) {
    if (!date || !isDayDisabled(date)) {
      onDaySelected && onDaySelected(date)
    }
  }

  function handleMonthChange (date) {
    updateFocusState({ focusedDay: date })
  }

  function isDayDisabled (date) {
    const momentDate = moment(date)
    return (
      (minDate && momentDate.isBefore(minDate, 'day')) ||
      (maxDate && momentDate.isAfter(maxDate, 'day')) ||
      (disableDayFn && disableDayFn(date))
    )
  }

  function updateFocusState (newState) {
    if (onFocusChanged) {
      const {
        date: stateDate = date,
        focusedDay: stateFocusedDay = focusedDay
      } = newState
      const newDate = stateFocusedDay || stateDate
      onFocusChanged(`${idPrefix}-${moment(newDate).format(FORMAT)}`, newDate)
    }
    return setState(newState)
  }

  function updateFocusedDayRef () {
    if (onFocusedDayRef) onFocusedDayRef(focusedDayRef)
  }
}

const dateProp = PropTypes.instanceOf(Date)

DayPicker.propTypes = {
  minDate: dateProp,
  maxDate: dateProp,
  defaultDate: dateProp,
  date: dateProp,
  disableDayFn: PropTypes.func,
  onDaySelected: PropTypes.func,
  onFocusChanged: PropTypes.func,
  idPrefix: PropTypes.string,
  withKeyboardNavigation: PropTypes.bool
}

export default DayPicker
